import React from 'react';

interface SubjectWiseQuestionsProps {
  reportCard: any;
}

const SubjectWiseQuestions: React.FC<SubjectWiseQuestionsProps> = ({ reportCard }) => {
  if (!reportCard?.subject_wise_stats || !Array.isArray(reportCard.subject_wise_stats) || reportCard.subject_wise_stats.length === 0) {
    return null;
  }

  // Sort subjects by questions attempted (descending)
  const sortedStats = [...reportCard.subject_wise_stats].sort((a, b) => 
    parseInt(b.questions_attempted) - parseInt(a.questions_attempted)
  );

  // Find max questions attempted for progress bar calculation
  const maxQuestions = Math.max(...sortedStats.map(stat => parseInt(stat.questions_attempted)));

  // Color palette for subjects
  const colors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe',
    '#43e97b', '#38f9d7', '#ffecd2', '#fcb69f', '#a8edea', '#fed6e3'
  ];

  return (
    <div className="row g-4 mb-4">
      <div className="col-12">
        <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
          <div className="card-header bg-transparent border-0 pt-4">
            <h5 className="card-title fw-bold mb-0">
              <i className="fas fa-chart-bar me-2 text-primary"></i>
              Subject-wise Questions Attempted
            </h5>
            <p className="text-muted small mb-0">Total questions attempted across different subjects</p>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {sortedStats.map((stats: any, index: number) => {
                const questionsAttempted = parseInt(stats.questions_attempted);
                const progressPercentage = maxQuestions > 0 ? (questionsAttempted / maxQuestions) * 100 : 0;
                const color = colors[index % colors.length];
                
                return (
                  <div key={index} className="col-md-6 col-lg-4">
                    <div className="border rounded-3 p-3 h-100" style={{ 
                      borderColor: `${color}30 !important`,
                      backgroundColor: `${color}08`
                    }}>
                      {/* Subject Header */}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold mb-0 text-truncate" title={stats.subject_name}>
                          {stats.subject_name}
                        </h6>
                        <span 
                          className="badge fs-6 fw-bold px-2 py-1"
                          style={{ 
                            backgroundColor: color,
                            color: 'white'
                          }}
                        >
                          {questionsAttempted}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="progress mb-2" style={{ height: '6px' }}>
                        <div
                          className="progress-bar"
                          style={{ 
                            width: `${progressPercentage}%`,
                            backgroundColor: color
                          }}
                        ></div>
                      </div>

                      {/* Additional Stats */}
                      <div className="d-flex justify-content-between text-muted small">
                        <span>
                          <i className="fas fa-check-circle me-1" style={{ color: '#28a745' }}></i>
                          {stats.correct_answers} correct
                        </span>
                        <span>
                          <i className="fas fa-percentage me-1" style={{ color: color }}></i>
                          {parseFloat(stats.accuracy).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary Stats */}
            <div className="mt-4 pt-3 border-top">
              <div className="row text-center">
                <div className="col-4">
                  <div className="d-flex flex-column">
                    <span className="fw-bold text-primary fs-5">
                      {sortedStats.reduce((sum, stat) => sum + parseInt(stat.questions_attempted), 0)}
                    </span>
                    <small className="text-muted">Total Questions Attempted</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="d-flex flex-column">
                    <span className="fw-bold text-success fs-5">
                      {sortedStats.reduce((sum, stat) => sum + parseInt(stat.correct_answers), 0)}
                    </span>
                    <small className="text-muted">Total Correct</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="d-flex flex-column">
                    <span className="fw-bold text-info fs-5">
                      {sortedStats.length}
                    </span>
                    <small className="text-muted">Subjects Covered</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectWiseQuestions;